import { unwrapResult } from '@reduxjs/toolkit';
import { AccountInfoQueryKey } from '@root/hooks/account/useAccountInfo';
import { selectFlags } from '@root/slices/flagsmith/selectors';
import { checkFlagsmithAnonymousUser, getJwtUserId } from '@root/utils/cookies/cookies';
import { useQueryClient } from '@tanstack/react-query';
import { DebouncedFunc } from 'lodash';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { accountSlice } from '../slices/accountSlice';
import {
  clearAuthError,
  isDomainSSOEnabled,
  loginWithEmailAndOtp,
  loginWithEmailAndPassword,
  loginWithGoogleOneTap,
  selectAuthWithEmail,
  setAuthError,
  setToken,
  signUp
} from '../slices/authSlice';
import { DispatchType, OneTapTokenInfo } from '../v2/types';
import { SCRIBE_API_URL } from './apiUrl';
import { CursiveRecorder, isElectron } from './electron';
import errorLabels, { ErrorLabels, ErrorPayload } from './errorLabels';
import { isEmailValid } from './helper';
import parseDomain from './parseDomain';
import { signInWithGoogle } from './signInWithGoogle';
import { trackEvent, trackSignIn, trackSignup } from './tracking';
import { useDebounce } from './useDebounce';

const ssoSubmit = (email, apiUrl, macOSAppAuth, backToEmbed = false, superOrgId = null) => {
  const domain = parseDomain(email);
  let clientType = isElectron() ? 'desktop' : 'web';

  if (macOSAppAuth) {
    clientType = 'macos_desktop';
  }

  const ssoURL = new URL(`${apiUrl || SCRIBE_API_URL}workos_sso/auth/`);

  ssoURL.searchParams.append('domain', domain);
  ssoURL.searchParams.append('client_type', clientType);
  ssoURL.searchParams.append('email', email);
  ssoURL.searchParams.append('from_embed', backToEmbed ? 'true' : 'false');
  if (superOrgId) {
    ssoURL.searchParams.append('super_organization', superOrgId);
  }

  if (isElectron()) {
    CursiveRecorder().openURLInBrowser(ssoURL);
  } else {
    // Open in this tab to avoid redundant tabs
    window.open(ssoURL, '_self');
  }
};

export type SsoSignInType = 'none' | 'forced' | 'optional';

export const useDebouncedSsoEmailCheck = (delay = 500): [SsoSignInType, DebouncedFunc<(...args: any) => any>, string, boolean] => {
  const [ssoType, setSsoType] = useState<SsoSignInType>('none');
  const [apiUrl, setApiUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch<DispatchType>();

  const emailCheckForSso = useDebounce((email) => {
    if (email && isEmailValid(email)) {
      const domain = parseDomain(email);

      setLoading(true);

      // @ts-expect-error - Should be solved when migrated to React-Query
      dispatch(isDomainSSOEnabled({ domain, email }))
        .then(unwrapResult)
        .then((data) => {
          setLoading(false);
          if (data.is_sso_enabled_domain) {
            setSsoType(data.force_sso ? 'forced' : 'optional');
          } else {
            setSsoType('none');
          }
          if (data.backend_url) {
            setApiUrl(data.backend_url);
          }
        });
    } else {
      setSsoType('none');
    }
  }, delay);

  return [ssoType, emailCheckForSso, apiUrl, loading];
};

const handleSignup = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  dispatch,
  router,
  onErr,
  cloudflareToken = null,
  fsUUID = null,
  queryClient,
  inviteCode
) => {
  const userInfo = await dispatch(
    // @ts-expect-error - Should be solved when migrated to React-Query
    signUp({ email, password, confirmPassword: password, firstName, lastName, cloudflareToken, fsUUID, inviteCode })
  );

  if (userInfo.meta.requestStatus === 'rejected') {
    const errLabels = errorLabels(userInfo?.payload as ErrorPayload);

    onErr(errLabels);
  }

  if (userInfo.payload && userInfo.payload.token) {
    trackEvent('onboarding_created_account', {
      email
    });
    trackSignup('email', userInfo.payload.id);
    dispatch(accountSlice.actions.setAccountInfo(userInfo.payload));
    queryClient?.setQueryData?.(AccountInfoQueryKey, userInfo.payload); // update the account info in the cache
    // @ts-expect-error - TODO: Adjust param types
    dispatch(setToken({ token: userInfo.payload.token, refresh: userInfo.payload.refresh }));

    router.push('/welcome');
  }
};

const handleLogin = async (email, password, dispatch) => {
  try {
    // @ts-expect-error - Should be solved when migrated to React-Query
    await dispatch(loginWithEmailAndPassword({ email: email.toLowerCase(), password }))
      .then(unwrapResult)
      .then((tokenInfo) => {
        if (tokenInfo && (tokenInfo.token || tokenInfo.refresh)) {
          const userId = getJwtUserId(tokenInfo.token);

          trackSignIn('email', userId);
        }
      });
  } catch (err) {
    console.error('Failed to login', err);
  }
};

export const handleOneTapLogin = async (
  credential,
  dispatch: DispatchType,
  router,
  onEnableOneTapBottomCTA,
  onEnableOneTapContainerExperiment
) => {
  // @ts-expect-error - Should be solved when migrated to React-Query
  const userInfo: OneTapTokenInfo = await dispatch(loginWithGoogleOneTap({ credential }));

  if (userInfo?.payload?.token) {
    userInfo.token = userInfo.payload.token;
    // @ts-expect-error - TODO: Adjust param types
    dispatch(setToken({ token: userInfo.payload.token, refresh: userInfo.payload.refresh }));
    if (userInfo.payload.is_signup) {
      trackEvent('user_signed_up_google_one_tap');
      trackSignup('google_one_tap', userInfo.payload?.id);
      onEnableOneTapBottomCTA?.();
      onEnableOneTapContainerExperiment?.();
    } else {
      trackEvent('user_signed_in_google_one_tap', { userId: userInfo.payload?.id });
      trackSignIn('google_one_tap', userInfo.payload?.id);
    }
  }
};

interface useAuthenticationFormArgs {
  signup?: boolean;
  backToEmbed?: boolean;
  showName?: boolean;
  callback?: () => void;
  disableCloudflare?: boolean;
  overrideSsoType?: SsoSignInType;
}

export const useAuthenticationFormProps = ({
  signup = false,
  backToEmbed = false,
  showName = false,
  callback,
  disableCloudflare = false,
  overrideSsoType
}: useAuthenticationFormArgs): AuthenticationFormProps => {
  const router = useRouter();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const e2eEnv = typeof window !== 'undefined' ? window.localStorage.getItem('e2e') : false;
  const jestTestENV = process?.env?.JEST_WORKER_ID !== undefined;

  const { as, appauth: macOSAppAuth } = router.query;

  const { isLoading, errorMessage } = useSelector(selectAuthWithEmail);

  const anonymousUserCookieVal = checkFlagsmithAnonymousUser();
  const [cloudflareToken, setCloudflareToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string>((as as string) || '');
  const [fullName, setFullName] = useState<string>('');
  const [showPassword, setShowPassword] = useState(!!as);
  const [password, setPassword] = useState<string>('');
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [showOtp, setShowOtp] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<ErrorLabels>({});

  // Flag value is list of domains excluded from Cloudflare Turnstile
  // disable_cf_turnstile_list ["example.com", "test.com"]
  const { disableCfTurnstileList } = useSelector(selectFlags);
  const emailDomain = parseDomain(email);
  let disableList: string[] = [];

  if (Array.isArray(disableCfTurnstileList)) {
    disableList = disableCfTurnstileList;
  } else if (typeof disableCfTurnstileList === 'string') {
    try {
      const parsed = JSON.parse(disableCfTurnstileList);

      if (Array.isArray(parsed) && parsed.every((d) => typeof d === 'string')) {
        disableList = parsed;
      }
    } catch (e) {
      console.error('Failed to parse disableCfTurnstileList:', e);
    }
  }

  const turnstileDisabledByFlag = typeof emailDomain === 'string' && disableList.includes(emailDomain);

  const cloudflareTurnstileEnabled = !e2eEnv && !jestTestENV && !disableCloudflare && !turnstileDisabledByFlag;

  let [ssoType, emailCheckForSso, apiUrl, ssoCheckLoading] = useDebouncedSsoEmailCheck();

  ssoType = overrideSsoType || ssoType;

  const onEmailChange = (email) => {
    emailCheckForSso(email);
    setEmail(email);
  };
  const onPasswordChange = (password) => setPassword(password);
  const onNameChange = (name: string) => {
    const shouldShowPassword = showName && name.trim().length > 0 && ssoType !== 'forced';

    setShowPassword(shouldShowPassword);
    setFullName(name);
  };
  const onOtpChange = (otp) => setOtp(otp);

  const onGoogleSubmit = () => {
    // Assume Google SSO will succeed and simply call the callback early.
    // Since we only use callbacks for tracking, this should be okay.
    callback?.();
    signInWithGoogle((cookiesDisabled) => {
      // @ts-expect-error - Should be solved when migrated to React-Query
      dispatch(setAuthError(`Could not sign in with Google.${cookiesDisabled ? ' Third-party cookies are disabled.' : ''}`));
    });
  };
  const emailSubmit = getEmailSubmit(dispatch, router, signup, setFieldErrors, cloudflareToken, anonymousUserCookieVal, queryClient);
  const otpSubmit = getOtpSubmit(dispatch);

  const onSubmit = (options?: { superOrgId?: string; inviteCode?: string }) => {
    if (otp !== '') {
      otpSubmit(email, otp);
      return;
    }
    if (!showPassword && ssoType !== 'forced') {
      setShowPassword(true);
      return;
    }

    if (!password && ssoType !== 'forced') {
      return;
    }

    if (ssoType === 'forced') {
      ssoSubmit(email, apiUrl, macOSAppAuth, backToEmbed, options?.superOrgId);
      //  Show OTP fallback
      if (isElectron()) {
        setTimeout(() => setShowOtp(true), 3000);
      }
    } else {
      const fullNameCleaned = fullName.trim().replace(/\s+/g, ' ').split(' '); // removes extra spaces amd splits by space
      const firstName = fullNameCleaned[0] || ''; // first name or empty string
      const lastName = fullNameCleaned.slice(1).join(' ') || ''; // last name or empty string if no last name provided

      emailSubmit(email, password, firstName, lastName, callback, cloudflareToken, anonymousUserCookieVal, options?.inviteCode);
    }
  };

  const isCloudflareValid = !cloudflareTurnstileEnabled || cloudflareToken || turnstileDisabledByFlag;

  useEffect(() => {
    setDisableSubmit(
      email === '' ||
        !isEmailValid(email) ||
        (showPassword && password === '' && ssoType !== 'forced' && otp === '') ||
        !isCloudflareValid ||
        (showName && !fullName)
    );
  }, [email, password, showPassword, ssoType, otp, fullName, showName, cloudflareToken, isCloudflareValid]);

  useEffect(() => {
    dispatch(clearAuthError());
  }, [email, password]);

  // Always check for SSO if "as" is defined
  useEffect(() => {
    if (as) {
      emailCheckForSso(as);
    }
  }, []);

  return {
    cloudflareTurnstileEnabled,
    setCloudflareToken,
    email,
    onEmailChange,
    fullName,
    onNameChange,
    password,
    onPasswordChange,
    onSubmit,
    otp,
    onOtpChange,
    onSsoSubmit: (event?, options?: { superOrgId }) => {
      //  Show OTP fallback
      if (isElectron()) {
        setTimeout(() => setShowOtp(true), 3000);
      }
      ssoSubmit(email, apiUrl, macOSAppAuth, backToEmbed, options?.superOrgId);
    },
    onGoogleSubmit,
    showOtp,
    loading: isLoading,
    error: errorMessage,
    showPassword,
    showName,
    disableSubmit,
    ssoType,
    ssoCheckLoading,
    fieldErrors
  };
};

const getEmailSubmit = (dispatch, router, signup = false, onErr, cloudflareToken = null, fsUUID = null, queryClient) => {
  return async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    callback = undefined,
    cloudflareToken = null,
    fsUUID = null,
    inviteCode
  ) => {
    if (signup) {
      await handleSignup(email, password, firstName, lastName, dispatch, router, onErr, cloudflareToken, fsUUID, queryClient, inviteCode);
    } else {
      await handleLogin(email, password, dispatch);
    }

    callback?.();
  };
};

const getOtpSubmit = (dispatch) => {
  return async (email, otp, callback = undefined) => {
    // @ts-expect-error - Should be solved when migrated to React-Query
    await dispatch(loginWithEmailAndOtp({ email: email.toLowerCase(), otp }))
      .then(unwrapResult)
      .then((userInfo) => {
        if (userInfo && (userInfo.token || userInfo.refresh)) {
          trackSignIn('email+otp', userInfo.id);
        }
      });
    callback?.();
  };
};

export interface AuthenticationFormProps {
  cloudflareTurnstileEnabled?: boolean;
  setCloudflareToken?: (token: string | null) => void;
  email: string;
  onEmailChange: (email: string) => void;
  fullName?: string;
  onNameChange?: (name: string) => void;
  password: string;
  onPasswordChange: (password: string) => void;
  onSubmit?: (event?, options?: { superOrgId?: string; inviteCode?: string }) => void;
  onSsoSubmit?: (event?, options?: { superOrgId?: string }) => void;
  onGoogleSubmit?: () => void;
  otp?: string;
  onOtpChange?: (otp: string) => void;
  loading?: boolean;
  error?: string;
  showPassword?: boolean;
  showOtp?: boolean;
  disableSubmit?: boolean;
  ssoType: SsoSignInType;
  ssoCheckLoading?: boolean;
  hideSubmit?: boolean;
  redirectToDesktop?: boolean;
  fieldErrors?: ErrorLabels;
  backToEmbed?: boolean;
  newOnboarding?: boolean;
  showName?: boolean;
  leftAlign?: boolean;
  isSigningInWithSso?: boolean;
  disableEmailInput?: boolean;
}
